import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import { styled } from "@mui/system";
import { Link } from "react-router-dom";

import useApiEp from "../../hooks/useApiEp";
import { extractDate, extractTime, getErrorResponse } from "../../utils/util";
import BackBtn from "../../widgets/BackBtn";
import CustomCircularP from "../../widgets/CustomCircularP";
import SearchBar from "../../widgets/searchBar";
import ExportVisitorsButton from "./ExportVisitorsButton";

export const ScrollTableContainer = styled(TableContainer)({
  overflowX: "auto",
});

const Visitors = ({ type }) => {
  const [page, setPage] = useState(1);
  const [sourceFilter, setSourceFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sq, setSq] = useState("");

  const { data, isLoading, isError, error } = useApiEp(
    `/admin/get-visitors?type=${encodeURIComponent(
      type
    )}&page=${page}&sq=${encodeURIComponent(
      sq
    )}&sourceType=${encodeURIComponent(sourceFilter)}`
  );

  const { data: sourceList } = useApiEp("/event/get-visitor-sources");

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSq(searchInput.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const title = `${type.charAt(0).toUpperCase()}${type.slice(1)}s`;
  const totalItems = data?.totalItems || 0;
  const totalPages = data?.totalPages || 1;
  const hasVisitors = data?.data?.length > 0;
  const filtersActive = Boolean(searchInput || sourceFilter);
  const sources = Array.isArray(sourceList)
    ? sourceList
    : sourceList?.data || [];

  const getFullName = (user) =>
    [user.title, user.firstName, user.lastName].filter(Boolean).join(" ") ||
    "Unnamed visitor";

  const getInitials = (user) => {
    const initials = [user.firstName, user.lastName]
      .filter(Boolean)
      .map((name) => name[0])
      .join("")
      .toUpperCase();
    return initials || "V";
  };

  const getLocation = (user) =>
    [user.city?.name, user.state?.name, user.country?.name]
      .filter(Boolean)
      .join(", ") || "-";

  const clearFilters = () => {
    setSearchInput("");
    setSq("");
    setSourceFilter("");
    setPage(1);
  };

  const headerCellSx = {
    bgcolor: "#f8fafc",
    color: "text.secondary",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.04em",
    py: 1.5,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: 1600, mx: "auto" }}>
      <BackBtn bstyle={{ mb: 1.5, px: 0 }} />
      <CustomCircularP show={isLoading} />

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          mb: 2.5,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            background:
              "linear-gradient(135deg, rgba(2, 71, 95, 0.08) 0%, rgba(255, 255, 255, 0) 62%)",
          }}
        >
          <Stack direction="row" spacing={1.75} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <GroupsOutlinedIcon />
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  component="h1"
                  sx={{ fontSize: { xs: 24, sm: 28 }, fontWeight: 750 }}
                >
                  {title}
                </Typography>
                <Chip
                  label={totalItems.toLocaleString()}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Browse and manage registered {title.toLowerCase()}.
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <ExportVisitorsButton filter={type} sourceFilter={sourceFilter} />
          </Box>
        </Box>

        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={1.5}
          >
            <FormControl size="small" sx={{ minWidth: { md: 190 } }}>
              <InputLabel id="visitor-source-label">Source</InputLabel>
              <Select
                labelId="visitor-source-label"
                value={sourceFilter}
                label="Source"
                onChange={(event) => {
                  setSourceFilter(event.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">All sources</MenuItem>
                {sources.map((source, index) => (
                  <MenuItem key={`${source}-${index}`} value={source}>
                    {source}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ width: "100%", maxWidth: 560 }}>
              <SearchBar
                value={searchInput}
                placeholder="Search by name or email…"
                size="small"
                onChange={(event) => setSearchInput(event.target.value)}
                inputProps={{ "aria-label": `Search ${title.toLowerCase()}` }}
              />
            </Box>

            {filtersActive && (
              <Button
                size="small"
                color="inherit"
                startIcon={<FilterAltOffOutlinedIcon />}
                onClick={clearFilters}
                sx={{ whiteSpace: "nowrap", alignSelf: { md: "center" } }}
              >
                Clear filters
              </Button>
            )}

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: { md: "auto !important" }, whiteSpace: "nowrap" }}
            >
              {filtersActive
                ? `${totalItems.toLocaleString()} result${
                    totalItems === 1 ? "" : "s"
                  } found`
                : `${totalItems.toLocaleString()} total`}
            </Typography>
          </Stack>
        </Box>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {getErrorResponse(error)}
        </Alert>
      )}

      {!isError && (
        <ScrollTableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          <Table aria-label={`${title} directory`} sx={{ minWidth: 980 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Visitor</TableCell>
                <TableCell sx={headerCellSx}>Email</TableCell>
                <TableCell sx={headerCellSx}>Phone</TableCell>
                <TableCell sx={headerCellSx}>Location</TableCell>
                <TableCell sx={headerCellSx}>Joined</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && !hasVisitors ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 9 }}>
                    <SearchOffOutlinedIcon
                      sx={{ fontSize: 44, color: "text.disabled", mb: 1 }}
                    />
                    <Typography variant="subtitle1" fontWeight={700}>
                      {filtersActive
                        ? `No ${title.toLowerCase()} match your filters`
                        : `No ${title.toLowerCase()} yet`}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {filtersActive
                        ? "Try another search or clear the active filters."
                        : "New registrations will appear here."}
                    </Typography>
                    {filtersActive && (
                      <Button size="small" sx={{ mt: 1.5 }} onClick={clearFilters}>
                        Clear filters
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                data?.data?.map((user, index) => (
                  <TableRow
                    key={user._id || index}
                    hover
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "& td": { py: 1.5 },
                    }}
                  >
                    <TableCell sx={{ minWidth: 250 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          src={user.profile_image || ""}
                          alt={getFullName(user)}
                          sx={{
                            width: 42,
                            height: 42,
                            bgcolor: "rgba(2, 71, 95, 0.10)",
                            color: "primary.main",
                            fontSize: 14,
                            fontWeight: 700,
                          }}
                        >
                          {getInitials(user)}
                        </Avatar>
                        <Typography
                          component={Link}
                          to={`/visitor-view/${user._id}`}
                          variant="body2"
                          sx={{
                            color: "primary.main",
                            fontWeight: 700,
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {getFullName(user)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {user.email ? (
                        <Box
                          component="a"
                          href={`mailto:${user.email}`}
                          sx={{
                            color: "text.primary",
                            textDecoration: "none",
                            "&:hover": { color: "primary.main" },
                          }}
                        >
                          {user.email}
                        </Box>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {user.mobileNo || "-"}
                    </TableCell>
                    <TableCell sx={{ minWidth: 240 }}>
                      {getLocation(user)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography variant="body2">
                        {extractDate(user.createdOn) || "-"}
                      </Typography>
                      {user.createdOn && (
                        <Typography variant="caption" color="text.secondary">
                          {extractTime(user.createdOn)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollTableContainer>
      )}

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3} mb={2}>
          <Pagination
            page={page}
            onChange={(_, nextPage) => setPage(nextPage)}
            count={totalPages}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default Visitors;
